
import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { processedUrl } from '@padlet/vivaldi-client'
import { calculateSizeToFitElementInWrapper } from 'bits/attachments'
import { Component, Getter, namespace, Prop, Vue, Watch } from 'bits/vue'
import type { Cid } from 'types'
import SurfacePanelPostExpandedAttachment from 'vuecomponents/SurfacePanelPostExpandedAttachment.vue'

@Component({
  components: { SurfacePanelPostExpandedAttachment },
})
export default class SurfacePanelPostExpandedAttachmentWithPreloader extends Vue {
  $refs!: {
    attachment: SurfacePanelPostExpandedAttachment
  }

  @Prop({ required: true }) darkMode!: true | false | 'auto'
  @Prop({ required: true }) displayAttributes!: BeethovenDisplayAttributes | null
  /**
   * Safe area is an area in the top left corner of the attachment area
   * that the attachment can't be displayed on top of.
   */
  @Prop() safeArea?: { width: number; height: number }

  @Getter blockedContentProviders!: Set<string>

  /**
   * PRELOADING
   */

  @namespace('postAction').Getter expandedPostPreviousPostCid!: Cid | null
  @namespace('postAction').Getter expandedPostNextPostCid!: Cid | null
  @namespace('attachment').Getter linkDisplayAttributesForPost!: (postCid: Cid) => BeethovenDisplayAttributes | null
  @namespace('attachment').Action fetchDisplayAttributesForPost!: (postCid: Cid) => Promise<void>

  @Watch('expandedPostPreviousPostCid', { immediate: true })
  onPreviousPostCidChange(postCid: Cid): void {
    this.preloadAttachmentForPost(postCid)
  }

  @Watch('expandedPostNextPostCid', { immediate: true })
  onNextPostCidChange(postCid: Cid): void {
    this.preloadAttachmentForPost(postCid)
  }

  preloadAttachmentForPost(postCid: Cid): void {
    this.$nextTick().then(async () => {
      await this.fetchDisplayAttributesForPost(postCid)
      const displayAttributes = this.linkDisplayAttributesForPost(postCid)
      if (displayAttributes?.content_category === 'photo') {
        this.preloadImage(displayAttributes)
      }
    })
  }

  preloadImage(displayAttributes: BeethovenDisplayAttributes): void {
    const wrapper = {
      width: this.$refs.attachment.$refs.wrapper.offsetWidth,
      height: this.$refs.attachment.$refs.wrapper.offsetHeight,
    }

    const imgUrl = displayAttributes.original_image_url || displayAttributes.url
    const imgWidth = displayAttributes.original_image_width as number
    const imgHeight = displayAttributes.original_image_height as number
    const aspectRatio = imgWidth / imgHeight
    const element = {
      width: imgWidth,
      height: imgHeight,
      aspectRatio,
    }

    // Calculate how the image should be displayed in the wrapper.
    const { width, height } = calculateSizeToFitElementInWrapper(element, wrapper)
    // Make sure we are sending the exact same params to `processedUrl` as we do in
    // `ImageThumbnail` so that we get the same URL as if the image is being rendered
    // by that component. Otherwise, we would be preload the wrong image.
    const preloadUrl = processedUrl(imgUrl, { width, height })
    const img = new Image()
    // The browser will immediately trigger a network request and the image will
    // be loaded and cached.
    img.src = preloadUrl
  }
}
